import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect, useCallback } from "react";

import api from "api/api";
import endpoints from "api/endpoints";
import { ErrorMessage } from "common";

import { Icon, IconButton } from "@/design-system";

// The reference's `_brandLabel` (grep it in `Dens N Dente Tailwind.dc.html`):
// short brand words, "Card" for anything it doesn't know.
const BRAND_LABEL = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "Amex",
  discover: "Discover",
};

const brandLabel = (brand) => BRAND_LABEL[brand] || "Card";

/**
 * The saved-card picker used by the checkout Payment step, `StripeCheckout`
 * and the profile's Payment settings card. Rows follow the reference's
 * payment-method chooser (its invoice Pay dialog, `payMethods`): a leading
 * blue `credit_card` glyph, "Visa ending 4242" + a `text-xs` ink-3 line, and a
 * trailing radio glyph; the chosen row wears `border-blue-300` + `bg-soft`
 * (radio blue, like the address book — orange is for Delivery/Payment tabs).
 * Remove is an app adaptation (the reference's chooser has no remove; its
 * account cards do), so it is the account grid's delete IconButton — pass
 * `allowRemove={false}` for a pure picker (e.g. the order-detail Pay-invoice
 * dialog, where removing a card mid-payment makes no sense).
 *
 * No outer card chrome: every consumer already renders inside a
 * `CheckoutSectionCard` / `SettingsCard`.
 */
export default function SavedPaymentMethods({
  customerId,
  onSelectPaymentMethod,
  selectedPaymentMethodId,
  showTitle = true,
  allowRemove = true,
  onPaymentMethodsChange,
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await api.get(
        endpoints.GET_PAYMENT_METHODS(customerId),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const methods = response.data.paymentMethods || [];
      setPaymentMethods(methods);
      onPaymentMethodsChange?.(methods);
    } catch (err) {
      setError("Failed to load payment methods");
    } finally {
      setLoading(false);
    }
  }, [customerId, getAccessTokenSilently, onPaymentMethodsChange]);

  useEffect(() => {
    if (customerId) {
      fetchPaymentMethods();
    }
  }, [customerId, fetchPaymentMethods]);

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    try {
      const token = await getAccessTokenSilently();
      await api.delete(endpoints.DELETE_PAYMENT_METHOD(paymentMethodId), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPaymentMethods((prev) =>
        prev.filter((pm) => pm.id !== paymentMethodId)
      );

      // If the removed method was selected, clear selection
      if (selectedPaymentMethodId === paymentMethodId) {
        onSelectPaymentMethod(null);
      }
    } catch (err) {
      setError("Failed to remove payment method");
    }
  };

  const select = (pm) => onSelectPaymentMethod(pm.id, pm);

  if (loading) {
    return (
      <div className="flex flex-col gap-[var(--space-2)]">
        <div className="dnd-skel h-[62px] rounded-md" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <div className="flex flex-col gap-[var(--space-3)]">
      {showTitle && (
        <h4 className="text-sm font-bold">Saved payment methods</h4>
      )}

      {paymentMethods.length === 0 ? (
        <div className="flex flex-col items-center gap-[var(--space-2)] rounded-md border border-dashed border-line bg-[var(--neutral-50)] px-[var(--space-4)] py-[var(--space-6)] text-center">
          <Icon name="credit_card" size={28} className="text-ink-3" />
          <div className="text-sm font-semibold">No cards saved yet</div>
          <div className="text-xs text-ink-3">
            Add a payment method to pay by card.
          </div>
        </div>
      ) : (
        <div
          role="radiogroup"
          aria-label="Saved payment methods"
          className="flex flex-col gap-[var(--space-2)]"
        >
          {paymentMethods.map((pm) => {
            const isSelected = selectedPaymentMethodId === pm.id;
            const expiry = `${String(pm.card.exp_month).padStart(2, "0")}/${pm.card.exp_year}`;
            const holder = pm.billing_details?.name;
            return (
              <div
                key={pm.id}
                role="radio"
                tabIndex={0}
                aria-checked={isSelected}
                onClick={() => select(pm)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    select(pm);
                  }
                }}
                className={`flex cursor-pointer items-center gap-[12px] rounded-md border px-[14px] py-[11px] text-left [transition:border-color_var(--duration-fast),background_var(--duration-fast)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)] ${
                  isSelected
                    ? "border-blue-300 bg-soft"
                    : "border-line-subtle bg-card hover:border-line"
                }`}
              >
                <Icon
                  name="credit_card"
                  size={22}
                  className="shrink-0 text-blue-600"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-ink">
                    {brandLabel(pm.card.brand)} ending {pm.card.last4}
                  </div>
                  <div className="truncate text-xs text-ink-3">
                    Expires {expiry}
                    {holder ? ` · ${holder}` : ""}
                  </div>
                </div>
                <Icon
                  name={
                    isSelected
                      ? "radio_button_checked"
                      : "radio_button_unchecked"
                  }
                  size={21}
                  className={`shrink-0 ${isSelected ? "text-blue-600" : "text-ink-3"}`}
                />
                {allowRemove && (
                  <IconButton
                    icon="delete"
                    size="sm"
                    aria-label={`Remove ${brandLabel(pm.card.brand)} ending ${pm.card.last4}`}
                    className="shrink-0 enabled:hover:text-error-600"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleRemovePaymentMethod(pm.id);
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
